import './chat-message.scss';

import { first, isEmpty, kebabCase, lowerCase } from 'lodash';
import { Fragment, MouseEvent, useCallback, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { v4 as uuid } from 'uuid';

import {
  ChatMessageActionStatus,
  ChatMessageActionType,
  ChatMessageAppointmentStatus,
  ChatMessagePostedByType,
  ChatMessageType,
} from '../../../data/data/enums';
import { ChatMessageItem } from '../../../data/data/types';
import {
  ConsultType,
  ConversationStatus,
  MessageStatus,
} from '../../../data/types/api/graphql';
import {
  formatDate,
  formatDateTimeToTime,
} from '../../../data/utils/date.utils';
import { useFormatters } from '../../../data/utils/formatters.utils';
import {
  FileTypeImage,
  formatMimeType,
  getBase64FromServer,
  isValidFileType,
  onHandleDownloadFile,
} from '../../../data/utils/upload.utils';
import {
  ContextMenuHorizontalPosition,
  useContextMenu,
} from '../../../data/utils/use-context-menu';
import { Button, ButtonIcon, ButtonType } from '../../button/button';
import {
  ContextMenu,
  ContextMenuItemIcon,
} from '../../context-menu/context-menu';
import { CustomFormattedDate } from '../../intl/custom-formatted-date';
import { CustomFormattedMessage } from '../../intl/custom-formatted-message';
import { Spinner } from '../../spinner/spinner';
import { ChatMessageMindFeedbackForm } from '../chat-message-mind-feedback-form/chat-message-mind-feedback-form';
import { ChatMessageDeletePrompt } from './modals/chat-message-delete-prompt/chat-message-delete-prompt';
import { ChatMessageFilesPreviewModal } from './modals/chat-message-files-preview-modal/chat-message-files-preview-modal';

export enum ChatMessagePosition {
  DEFAULT = 'DEFAULT',
  LEFT = 'LEFT',
  RIGHT = 'RIGHT',
}

type ChatMessageProps = {
  position?: ChatMessagePosition;
  received?: boolean;
  messageItem: ChatMessageItem;
  active?: string[];
  removable?: boolean;
  onClickText?: (
    messageItem: ChatMessageItem,
    text: string | null | undefined,
    id?: string
  ) => void;
  onClickAppointment?: (messageItem: ChatMessageItem) => void;
};

const messageStatusToIcon = (status: MessageStatus) => {
  switch (status) {
    case MessageStatus.Maybe:
      return 'circle-dashed';
    case MessageStatus.Yes:
      return 'circle-check';
    case MessageStatus.No:
      return 'circle-x';
    default:
      return '';
  }
};

export function ChatMessage({
  position = ChatMessagePosition.DEFAULT,
  received = false,
  messageItem,
  removable = false,
  onClickText,
  onClickAppointment,
  ...props
}: ChatMessageProps) {
  const intl = useIntl();

  const [deleteModalOpen, setDeleteModalOpen] = useState<boolean>(false);
  const [filePreviewModalOpen, setFilePreviewModalOpen] = useState<
    number | false
  >(false);

  const [previews, setPreviews] = useState<{ [key: string]: string }>({});

  const { getFullName, getFullNameAndHonorific } = useFormatters();

  const changes = useMemo(() => {
    if (messageItem.change) {
      let statusLabel = '';
      let fromLabel = '';
      let toLabel = '';

      // Determine status type (Urgency or Status)
      if (
        typeof messageItem.change.from === 'boolean' &&
        typeof messageItem.change.to === 'boolean'
      ) {
        // Urgency change
        statusLabel = intl.formatMessage({ id: 'common.urgency' });
        fromLabel = intl
          .formatMessage({
            id: messageItem.change.from ? 'common.urgent' : 'common.not-urgent',
          })
          .toLowerCase();
        toLabel = intl
          .formatMessage({
            id: messageItem.change.to ? 'common.urgent' : 'common.not-urgent',
          })
          .toLowerCase();
      } else if (
        typeof messageItem.change.from !== 'boolean' &&
        typeof messageItem.change.to !== 'boolean' &&
        Object.values(ConversationStatus).includes(messageItem.change.from) &&
        Object.values(ConversationStatus).includes(messageItem.change.to)
      ) {
        // Status change
        statusLabel = intl.formatMessage({ id: 'common.status' });
        fromLabel = intl
          .formatMessage({
            id: `common.conversation.${kebabCase(messageItem.change.from as string)}`,
          })
          .toLowerCase();
        toLabel = intl
          .formatMessage({
            id: `common.conversation.${kebabCase(messageItem.change.to as string)}`,
          })
          .toLowerCase();
      }

      if (statusLabel && fromLabel && toLabel) {
        return (
          <CustomFormattedMessage
            id="common.conversation.changed"
            values={{
              status: statusLabel,
              from: fromLabel,
              to: toLabel,
              healthProfessional: getFullName(messageItem.postedBy),
              time: `${intl.formatDate(messageItem.postedAt, {
                day: '2-digit',
                month: 'long',
                year: 'numeric',
              })}  -  ${intl.formatTime(messageItem.postedAt, {
                hour: '2-digit',
                minute: '2-digit',
              })}`,
            }}
          />
        );
      }
    }
    return undefined;
  }, [
    messageItem.change,
    messageItem.postedAt,
    messageItem.postedBy,
    intl,
    getFullName,
  ]);

  const appointment = useMemo(() => {
    if (messageItem.appointment) {
      const values = {
        date: intl.formatDate(messageItem.appointment.startTime, {
          day: '2-digit',
          month: 'long',
          year: 'numeric',
        }),
        startTime: formatDateTimeToTime(
          messageItem.appointment.startTime,
          intl
        ),
        endTime: formatDateTimeToTime(messageItem.appointment.endTime, intl),
      };

      return {
        ...messageItem.appointment,
        title: (
          <CustomFormattedMessage
            id={`conversation.${kebabCase(messageItem.type)}.title`}
          />
        ),
        type: (
          <CustomFormattedMessage
            id={`common.agenda-appointment-type.${lowerCase(
              messageItem.appointment.type
            )}`}
          />
        ),
        date: (
          <CustomFormattedMessage
            id={
              messageItem.appointment.type === ConsultType.Telephone ||
              messageItem.appointment.type === ConsultType.Visit
                ? 'common.appointment.date-time'
                : 'common.appointment.date'
            }
            values={values}
          />
        ),
      };
    }

    return null;
  }, [messageItem.type, messageItem.appointment, intl]);

  const onLoadPreview = useCallback(
    (id: string, url: string | null | undefined) => {
      if (url) {
        getBase64FromServer(url).then((base64: string) => {
          setPreviews(prevState => ({
            ...prevState,
            [id]: base64,
          }));
        });

        return true;
      }

      return false;
    },
    []
  );

  const { files, totalFiles, download } = useMemo(() => {
    const formattedFiles = (messageItem.files ?? [])
      .slice(0, 4)
      .map((option, index) => {
        const id = uuid();

        return {
          id,
          name: option.title,
          file: option.file,
          type: formatMimeType(option.file),
          isImage: isValidFileType(formatMimeType(option.file), FileTypeImage),
          onClick: (e: MouseEvent<HTMLElement>) => {
            e.stopPropagation();
            e.preventDefault();

            setFilePreviewModalOpen(index);
          },
          preview: onLoadPreview(id, option.thumbnail),
        };
      });

    let formattedDownload = null;

    if (formattedFiles.length === 1) {
      const formattedDownloadFile = first(formattedFiles);

      if (formattedDownloadFile && !formattedDownloadFile.isImage) {
        formattedDownload = {
          ...formattedDownloadFile,
          onClick: (e: MouseEvent<HTMLElement>) => {
            e.stopPropagation();
            e.preventDefault();

            onHandleDownloadFile(formattedDownloadFile.file);
          },
        };
      }
    }

    return {
      files: formattedFiles,
      totalFiles: (messageItem.files ?? []).length,
      download: formattedDownload,
    };
  }, [messageItem.files, onLoadPreview]);

  const actions = useMemo(() => {
    const icons = {
      [ChatMessageActionType.URINE_QUESTIONNAIRE]: ButtonIcon.COMMENT,
      [ChatMessageActionType.EXPLAIN_COMPLAINT]: ButtonIcon.COMMENT,
      [ChatMessageActionType.APPOINTMENT]: ButtonIcon.CALENDAR,
    };

    return (messageItem.actions ?? []).map(option => {
      let action: boolean = true;
      let description: JSX.Element | null = null;

      const consultTypeMessage =
        option.appointment?.consultTypes &&
        option.appointment?.consultTypes.length === 1
          ? intl
              .formatMessage({
                id: `common.${kebabCase(option.appointment?.consultTypes[0])}`,
              })
              .toLowerCase()
          : '';

      if (
        option.type === ChatMessageActionType.APPOINTMENT &&
        option.status &&
        messageItem.showAppointmentConfirmation &&
        [
          ChatMessageActionStatus.COMPLETED,
          ChatMessageActionStatus.INACTIVE,
        ].includes(option.status)
      ) {
        action = false;

        description = (
          <CustomFormattedMessage id="conversation.chat-message.appointment-created" />
        );
      } else if (option.healthProfessional) {
        description = (
          <CustomFormattedMessage
            id="conversation.chat-message.plan-appointment-with"
            values={{
              types: consultTypeMessage,
              healthProfessional: getFullNameAndHonorific(
                option.healthProfessional
              ),
            }}
          />
        );
      } else if (option.appointment?.healthProfessional) {
        description = (
          <CustomFormattedMessage
            id="conversation.chat-message.plan-appointment-with"
            values={{
              types: consultTypeMessage,
              healthProfessional: getFullNameAndHonorific(
                option.appointment.healthProfessional
              ),
            }}
          />
        );
      } else if (option.appointment?.honorifics?.length) {
        description = (
          <CustomFormattedMessage
            id={
              option.appointment?.honorifics?.length > 1
                ? 'conversation.chat-message.plan-appointment-with-honorifics'
                : 'conversation.chat-message.plan-appointment-with-honorific'
            }
            values={{
              types: consultTypeMessage,
              honorifics:
                option.appointment.honorifics.length > 1
                  ? option.appointment.honorifics
                      .map(h =>
                        intl
                          .formatMessage({
                            id: `common.honorific.${kebabCase(h)}`,
                          })
                          .toLowerCase()
                      )
                      .join(', ')
                  : intl
                      .formatMessage({
                        id: `common.honorific.${kebabCase(
                          option.appointment.honorifics[0]
                        )}`,
                      })
                      .toLowerCase(),
            }}
          />
        );
      }

      return {
        id: uuid(),
        title: (
          <CustomFormattedMessage
            id={`conversation.schedule-appointment.${kebabCase(
              option.type
            )}.title`}
          />
        ),
        action,
        icon: icons[option.type as keyof typeof icons] ?? ButtonIcon.NONE,
        description,
        onClick: undefined,
      };
    });
  }, [
    messageItem.actions,
    messageItem.showAppointmentConfirmation,
    intl,
    getFullNameAndHonorific,
  ]);

  const { active, activeQa } = useMemo(() => {
    if ((props.active ?? []).length > 0) {
      if (
        [
          ChatMessageType.QA,
          ChatMessageType.APPOINTMENT_QA,
          ChatMessageType.MIND,
        ].includes(messageItem.type)
      ) {
        return {
          active: false,
          activeQa: props.active ?? [],
        };
      }

      return {
        active: true,
        activeQa: [],
      };
    }

    return {
      active: false,
      activeQa: [],
    };
  }, [messageItem, props.active]);

  const clickable = useMemo(
    () =>
      ![
        ChatMessageType.APPOINTMENT,
        ChatMessageType.APPOINTMENT_CANCELLED,
        ChatMessageType.APPOINTMENT_RESCHEDULED,
      ].includes(messageItem.type) && onClickText,
    [messageItem, onClickText]
  );

  const clickCallback = useMemo(
    () =>
      [
        ChatMessageType.APPOINTMENT,
        ChatMessageType.APPOINTMENT_RESCHEDULED,
      ].includes(messageItem.type)
        ? {
            role: 'button',
            tabIndex: 0,
            onClick: () => {
              onClickAppointment?.(messageItem);
            },
          }
        : {},
    [messageItem, onClickAppointment]
  );

  const onHandleText = useCallback(
    (value: string | null | undefined) => {
      onClickText?.(messageItem, value);
    },
    [messageItem, onClickText]
  );

  const onHandleQaText = useCallback(
    (id: string, value: string | null | undefined) => {
      onClickText?.(messageItem, value, `${messageItem.id}.${id}`);
    },
    [messageItem, onClickText]
  );

  const { contextMenuState, onOpenContextMenu } = useContextMenu({
    horizontal:
      position === ChatMessagePosition.LEFT
        ? ContextMenuHorizontalPosition.Right
        : ContextMenuHorizontalPosition.Left,
  });

  const questions = useMemo(
    () =>
      (messageItem.questions ?? []).map((option, index) => ({
        ...option,
        key: `${option.id}-${index}`,
      })),
    [messageItem.questions]
  );

  const postedBy = useMemo(() => {
    if (messageItem.postedBy.type === ChatMessagePostedByType.SYSTEM) {
      return <CustomFormattedMessage id="conversation.system-message" />;
    }
    if (
      messageItem.postedBy.type === ChatMessagePostedByType.GENERAL_PRACTICE
    ) {
      return (
        <CustomFormattedMessage id="conversation.general-practice-message" />
      );
    }
    return getFullName(messageItem.postedBy);
  }, [getFullName, messageItem.postedBy]);

  return (
    <>
      <div
        data-chat-message={messageItem.id}
        className={`chat-message chat-message--${kebabCase(
          position
        )} chat-message--${kebabCase(messageItem.type)}`}
      >
        {changes && <p className="changes">{changes}</p>}

        {!changes && (
          <div className="chat-message__content" {...clickCallback}>
            <h2>{postedBy}</h2>

            <div
              className={`message${clickable ? ' selectable' : ''}${
                active ? ' active' : ''
              }`}
            >
              {appointment && (
                <>
                  {appointment.title && (
                    <h3>
                      <i className="icon calendar" />
                      {appointment.title}:
                    </h3>
                  )}
                  <p className={`appointment ${kebabCase(appointment.status)}`}>
                    <strong>{appointment.type}</strong>
                    <br />
                    {appointment.date}
                  </p>

                  {appointment.status !==
                    ChatMessageAppointmentStatus.CANCELED && (
                    <>
                      {appointment.healthProfessional && (
                        <p className="health-professional">
                          <strong>
                            {getFullName(appointment.healthProfessional)}
                          </strong>
                          {appointment.healthProfessional.honorific && (
                            <>
                              <br />
                              (
                              <CustomFormattedMessage
                                id={`common.honorific.${kebabCase(
                                  appointment.healthProfessional.honorific
                                )}`}
                              />
                              )
                            </>
                          )}
                        </p>
                      )}

                      {appointment.healthProvider && (
                        <p className="health-provider">
                          {appointment.healthProvider.name}
                          <br />
                          {[
                            appointment.healthProvider.street,
                            [
                              appointment.healthProvider.houseNumber,
                              appointment.healthProvider.addition,
                            ]
                              .filter(option => !isEmpty(option))
                              .join(''),
                          ]
                            .filter(option => !isEmpty(option))
                            .join(' ')}
                          <br />
                          {[
                            appointment.healthProvider.postalCode,
                            appointment.healthProvider.city,
                          ]
                            .filter(option => !isEmpty(option))
                            .join(' ')}
                        </p>
                      )}
                    </>
                  )}
                  {appointment.message && (
                    <p>
                      <b>{appointment.message}</b>
                    </p>
                  )}
                </>
              )}

              {messageItem.type === ChatMessageType.TEXT &&
                isEmpty(messageItem.message) && (
                  <p className="empty">
                    <CustomFormattedMessage id="common.empty-message" />
                  </p>
                )}
              {messageItem.type === ChatMessageType.TEXT &&
                !isEmpty(messageItem.message) && (
                  <p
                    {...(clickable
                      ? {
                          className: `selectable${active ? ' active' : ''}`,
                          role: 'button',
                          tabIndex: 0,
                          onClick: () => onHandleText(messageItem.message),
                        }
                      : undefined)}
                  >
                    {messageItem.message}
                  </p>
                )}

              {![ChatMessageType.TEXT].includes(messageItem.type) &&
                messageItem.message && <p>{messageItem.message}</p>}

              {messageItem.type === ChatMessageType.STATUS &&
                messageItem.status && (
                  <h3
                    className={`status status--${kebabCase(messageItem.status)}`}
                  >
                    <i
                      className={`icon ${messageStatusToIcon(
                        messageItem.status
                      )}`}
                    />
                    <CustomFormattedMessage
                      id={`common.status.${kebabCase(messageItem.status)}`}
                    />
                  </h3>
                )}

              {messageItem.advice &&
                messageItem.id &&
                messageItem.advice.otherPerson && (
                  <h2 className="other-person">
                    <CustomFormattedMessage
                      id="conversation.chat-message.advice-for"
                      values={{
                        name: messageItem.advice.otherPerson.name,
                        birthdate: formatDate(
                          messageItem.advice.otherPerson.birthdate,
                          'dd-mm-YYYY'
                        ),
                        gender: messageItem.advice.otherPerson.gender
                          ? intl
                              .formatMessage({
                                id: `common.gender.${lowerCase(messageItem.advice.otherPerson.gender)}`,
                              })
                              .charAt(0)
                          : undefined,
                      }}
                    />
                  </h2>
                )}

              {questions.map(option => (
                <p
                  key={option.key}
                  {...(clickable
                    ? {
                        className: `selectable${
                          activeQa.includes(option.id) ? ' active' : ''
                        }`,
                        role: 'button',
                        tabIndex: 0,
                        onClick: () =>
                          onHandleQaText(
                            option.id,
                            `${option.question}\n${option.answer}`
                          ),
                      }
                    : undefined)}
                >
                  <strong>{option.question}</strong>
                  <br />
                  {option.answer}
                </p>
              ))}

              {messageItem.advice && messageItem.id && (
                <ChatMessageMindFeedbackForm
                  messageId={messageItem.id}
                  text={`${messageItem.advice.sessionSummary?.characteristicAdvice}`}
                  readonly={messageItem.advice.readonly}
                  feedback={messageItem.advice.feedback}
                />
              )}

              {download ? (
                <>
                  {download.name && <p>{download.name}</p>}

                  <p>
                    <Button
                      icon={ButtonIcon.DOWNLOAD}
                      inverted
                      disabled={!download.onClick}
                      onClick={download.onClick}
                    >
                      <CustomFormattedMessage id="common.chat-message.download" />
                    </Button>
                  </p>
                </>
              ) : (
                files.length > 0 && (
                  <div className="thumbnails">
                    {files.map((option, index) => {
                      const preview =
                        previews[option.id as keyof typeof previews];

                      return (
                        <div
                          key={option.id}
                          className="thumbnail"
                          {...(option.onClick
                            ? {
                                role: 'button',
                                tabIndex: 0,
                                onClick: option.onClick,
                              }
                            : undefined)}
                        >
                          <span className={`preview ${option.type}`}>
                            {option.isImage &&
                              (!preview ? (
                                <Spinner />
                              ) : (
                                <img src={preview} alt={option.name ?? ''} />
                              ))}

                            {!option.isImage && (
                              <span className="title">{option.name}</span>
                            )}

                            {index === 3 && totalFiles - files.length > 0 && (
                              <span className="count">
                                +{totalFiles - files.length}
                              </span>
                            )}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                )
              )}

              {actions.map(option => (
                <Fragment key={option.id}>
                  {option.action ? (
                    <>
                      <p>
                        <Button
                          icon={option.icon}
                          inverted
                          disabled={!option.onClick}
                          onClick={option.onClick}
                        >
                          {option.title}
                        </Button>
                      </p>

                      {option.description && <p>{option.description}</p>}
                    </>
                  ) : (
                    option.description && (
                      <p>
                        <strong>{option.description}</strong>
                      </p>
                    )
                  )}
                </Fragment>
              ))}
            </div>

            <p className={`info${received ? ' received' : ''}`}>
              <CustomFormattedDate value={messageItem.postedAt} showTime />
            </p>
          </div>
        )}

        {!changes && (
          <div className="chat-message__actions">
            <Button
              type={ButtonType.TRANSPARENT}
              icon={ButtonIcon.ELLIPSIS_VERTICAL}
              onClick={onOpenContextMenu}
            />
          </div>
        )}
      </div>

      {contextMenuState && (
        <ContextMenu
          state={contextMenuState}
          items={[
            {
              key: 'delete',
              icon: ContextMenuItemIcon.Delete,
              title: <CustomFormattedMessage id="common.delete" />,
              disabled: !removable,
              onClick: () => setDeleteModalOpen(true),
            },
          ]}
        />
      )}

      {deleteModalOpen && (
        <ChatMessageDeletePrompt
          messageItem={messageItem}
          onClose={() => setDeleteModalOpen(false)}
        />
      )}

      {typeof filePreviewModalOpen === 'number' && (
        <ChatMessageFilesPreviewModal
          messageItem={messageItem}
          slide={filePreviewModalOpen}
          onClose={() => setFilePreviewModalOpen(false)}
        />
      )}
    </>
  );
}
